import type { RosterMember } from "./api/roster";

/**
 * **What to call one member on a screen** — one table, because there were three (PR #336
 * review, finding 5).
 *
 * Two active members may share a display name, which the service supports deliberately: #92
 * compares subjects by `memberId` for exactly this reason. Rendering the name alone gives two
 * different people the same label, and the cost lands wherever a person is *chosen* — a
 * durable fact assigned to the wrong Sam, an item handed to the wrong Sam, or a filter chip
 * whose twin reveals somebody else's rows.
 *
 * `FactForm` and `ItemForm` each carried their own copy; the review found the third place that
 * needed it, `PlateFilters`. Three copies of a rule about identity is how one of them stops
 * matching, so it is one function.
 *
 * **The list row's owner chip deliberately does not use this**, which is where the rule stops.
 * That chip renders the name the *server* resolved, and it is a label rather than a control:
 * an ambiguous label costs a moment's thought, where an ambiguous *chip* costs a family the
 * ability to use the filter at all. It also keeps the row from needing the roster to render
 * something the read already answered. An earlier cut of this file carried a `memberLabelById`
 * for that path and nothing ever called it — a helper written for a decision that went the
 * other way, which is worth deleting rather than leaving as a suggestion.
 *
 * **The address is only added where it is needed.** Appending it to everybody would put every
 * member's email into a picker on screens that do not otherwise show one, and the common case
 * is names that are already distinct.
 */
export function memberLabel(member: RosterMember, members: readonly RosterMember[]): string {
  const shared = members.filter((other) => other.name === member.name).length > 1;
  return shared && member.email !== undefined ? `${member.name} (${member.email})` : member.name;
}
